import { Box, Sheet, Stack, Typography } from "@mui/joy";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridColumns, GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { useParentSize } from "@visx/responsive";
import { Circle, LinePath } from "@visx/shape";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { bisectCenter, extent, utcMonday, utcMonth, utcYear } from "d3";
import { stack as d3Stack, area as d3Area } from "d3";
import { ScaleBand, scaleBand, scaleLinear, scaleTime } from "d3-scale";
import { format } from "date-fns";
import omit from "lodash/omit";
import { useCallback, useMemo } from "react";

import formatNumber from "@/utils/formatNumber";

import ColorStripesPattern from "../Analyse/ColorStripesPattern";
import { RouteCircle } from "../RouteCircle";

export type TimeSeriesDatum = {
  x: Date;
  y: number;
};

export type TimeSeriesColumn = {
  label: string;
  key: string;
  colors: string;
  imageUrl?: string;
  data: TimeSeriesDatum[];
  isCompare?: boolean;
};

export type TimeSeriesData = {
  columns: TimeSeriesColumn[];
  totals: TimeSeriesDatum[];
  totalsCompare?: TimeSeriesDatum[];
  yLabel: string;
};

export type TimeSeriesGraphType =
  | "line"
  | "stackedBar"
  | "stackedArea"
  | "stacked100Bar"
  | "stacked100Area";

type TooltipColumn = Omit<TimeSeriesColumn, "data"> & {
  value: number;
  columnIndex: number;
};

type TooltipData = {
  x: Date;
  index: number;
  columns: TooltipColumn[];
  total: number;
};

type Props = {
  data: TimeSeriesData;
  type: TimeSeriesGraphType;
  period: "day" | "week" | "month";
};

function scaleBandInvert(scale: ScaleBand<Date>) {
  const domain = scale.domain();
  const paddingOuter = scale(domain[0]) || 0;
  const eachBand = scale.step();
  return function (value: number) {
    const index = Math.floor((value - paddingOuter) / eachBand);
    return domain[Math.max(0, Math.min(index, domain.length - 1))];
  };
}

export default function TimeSeriesGraph({
  data: { columns, totals, yLabel },
  type,
  period,
}: Props) {
  const { containerRef: svgRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
    zIndex: 1000,
  });

  const formatDateShort = useCallback(
    (d: Date) => {
      if (period === "day") {
        return format(d, "do MMM ''yy");
      }
      if (period === "week") {
        return format(d, "do MMM");
      }
      if (period === "month") {
        return format(d, "MMM ''yy");
      }
      return "";
    },
    [period],
  );

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<TooltipData>();

  const { parentRef, width, height } = useParentSize({
    debounceTime: 10,
  });

  console.log("G", { width, height });

  const marginTop = 10;
  const marginLeft = 60;
  const marginRight = 30;
  const marginBottom = 40;

  const xs = useMemo(() => {
    return totals.map((d) => d.x);
  }, [totals]);

  const xExtent = useMemo(() => {
    const [min = new Date(), max = new Date()] = extent(xs);
    return [min, max];
  }, [xs]);

  const isStack =
    type === "stackedBar" ||
    type === "stackedArea" ||
    type === "stacked100Bar" ||
    type === "stacked100Area";

  const stackedSeries = useMemo(() => {
    // ys should be an array of arrays, where each array is a set of y-values for a given x-value
    const ys = xs.map((_, i) => Object.fromEntries(columns.map((col) => [col.key, col.data[i].y])));
    const keys = columns.map((col) => col.key);
    return d3Stack().keys(keys)(ys);
  }, [columns, xs]);

  const xScale = useMemo(() => {
    return scaleTime(xExtent, [marginLeft, width - marginRight]);
  }, [xExtent, width]);

  const xBandScale = useMemo(() => {
    return scaleBand(xs, [marginLeft, width - marginRight])
      .paddingInner(0.05)
      .paddingOuter(0.05);
  }, [xs, width]);

  const yScale = useMemo(() => {
    let min = 0;
    let max = 0;
    if (isStack) {
      max = Math.max(...totals.map((d) => d.y));
    } else {
      const allData = columns.flatMap((col) => col.data);
      [min = 0, max = 0] = extent(allData, (d) => d.y);
    }

    return scaleLinear([min, max], [height - marginBottom, marginTop]);
  }, [isStack, height, totals, columns]);

  const areas = useMemo(() => {
    const areaFn = d3Area()
      .x((_d, i) => xScale(xs[i]))
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]));
    return stackedSeries.map((stack) => areaFn(stack as [number, number][]) || "");
  }, [stackedSeries, xScale, xs, yScale]);

  const xAxisScale = type === "stackedBar" || type === "stacked100Bar" ? xBandScale : xScale;

  let xTickValues;
  // if (type === "stackedBar" || type === "stacked100Bar") {
  //   xTickValues = xBandScale.
  // } else {
  const xDuration = xExtent[1].getTime() - xExtent[0].getTime();
  if (xDuration > 2 * 365 * 24 * 60 * 60 * 1000) {
    xTickValues = xScale.ticks(utcYear.every(1)!);
  } else if (xDuration > 1.5 * 365 * 24 * 60 * 60 * 1000) {
    xTickValues = xScale.ticks(utcMonth.every(2)!);
  } else if (xDuration > 90 * 24 * 60 * 60 * 1000) {
    xTickValues = xScale.ticks(utcMonth.every(1)!);
  } else {
    xTickValues = xScale.ticks(utcMonday.every(1)!);
  }
  // }

  return (
    <Box
      ref={parentRef}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 200,
        overflowX: "auto",
        overflowY: "hidden",
        flex: 1,
      }}
    >
      <svg
        ref={svgRef}
        width={width}
        height={height}
        onMouseMove={(e) => {
          const coords = localPoint(e.currentTarget, e);

          if (!coords) {
            return;
          }

          // get the x value from the xScale
          const mouseX =
            type === "stackedBar" || type === "stacked100Bar"
              ? scaleBandInvert(xBandScale)(coords.x)
              : xScale.invert(coords.x);
          // get the closest index to the x value
          const index = bisectCenter(xs, mouseX);
          const x0 = xs[index];

          let tooltipColumnData = columns.map((col, i) => ({
            ...omit(col, ["isCompare", "data"]),
            value: col.data[index]?.y,
            columnIndex: i,
          }));
          if (isStack) {
            // Reverse the order
            tooltipColumnData = tooltipColumnData.reverse();
          } else if (type === "line") {
            // Order by y value
            tooltipColumnData = tooltipColumnData.sort((a, b) => (a.value || 0) - (b.value || 0));
          }

          const tooltipData: TooltipData = {
            x: x0,
            index,
            columns: tooltipColumnData,
            total: totals[index]?.y,
          };

          if (x0) {
            showTooltip({
              tooltipLeft: coords.x,
              tooltipTop: coords.y,
              tooltipData,
            });
          }
        }}
        onMouseOut={hideTooltip}
      >
        <defs>
          {columns.map(({ key, colors: c }) => {
            const colors = (c || "#F1F1F4").split(":");
            if (colors.length === 1) {
              return null;
            }
            return <ColorStripesPattern key={key} id={`pattern-${key}`} colors={colors} />;
          })}
        </defs>
        <GridRows
          // top={marginTop}
          left={marginLeft}
          scale={yScale}
          width={width - marginLeft - marginRight}
          height={height - marginTop - marginBottom}
          stroke="#F1F1F4"
          strokeOpacity={1}
          strokeDasharray="4,2"
        />
        <GridColumns
          // top={height - marginBottom}
          scale={xAxisScale}
          height={height - marginTop - marginBottom}
          stroke="#F1F1F4"
          strokeOpacity={1}
          strokeDasharray="4,2"
        />
        <AxisLeft
          scale={yScale}
          // top={marginTop}
          left={marginLeft}
          label={yLabel}
          labelProps={{
            fontSize: 13,
            textAnchor: "middle",
            fontWeight: "bold",
            fontFamily: "IBM Plex Sans",
          }}
          numTicks={5}
          // tickValues={yScale.ticks()}
          // tickFormat={yScale.tickFormat(10, "s")}
          tickLength={6}
          tickLabelProps={() => ({
            fontSize: 11,
            textAnchor: "end",
            verticalAnchor: "middle",
            fontWeight: "bold",
            fontFamily: "IBM Plex Sans",
          })}
          stroke="#C5C9D3"
          tickStroke="transparent"
        />
        <AxisBottom
          top={height - marginBottom}
          // left={marginLeft}
          scale={xAxisScale}
          tickValues={xTickValues}
          // numTicks={columns[0].data.length}
          // tickValues={xScale.ticks()}
          // tickFormat={xBandScale.tickFormat(10, "%b %d")}
          tickFormat={(d) => formatDateShort(d as Date)}
          tickLabelProps={{
            fontSize: 11,
            textAnchor: "middle",
            fontWeight: "bold",
            fontFamily: "IBM Plex Sans",
          }}
          tickStroke="transparent"
          tickLength={4}
          stroke="#C5C9D3"
        />
        {type === "line"
          ? columns.map((col) => (
              <Group key={col.key}>
                <LinePath
                  data={col.data}
                  x={(d) => xScale(d.x) ?? 0}
                  y={(d) => yScale(d.y) ?? 0}
                  stroke={"#000"}
                  strokeOpacity={0.1}
                  strokeWidth={4}
                />
                <LinePath
                  data={col.data}
                  x={(d) => xScale(d.x) ?? 0}
                  y={(d) => yScale(d.y) ?? 0}
                  stroke={col.colors}
                  strokeWidth={2}
                />
              </Group>
            ))
          : null}
        {type === "stackedArea" || type === "stacked100Area" ? (
          <Group>
            {areas.map((area, i) => {
              const colors = columns[i].colors.split(":");
              const fill = colors.length > 1 ? `url(#pattern-${columns[i].key})` : colors[0];
              return (
                <path
                  key={i}
                  d={area}
                  fill={fill}
                  strokeWidth={0.5}
                  strokeOpacity={0.15}
                  stroke="#000"
                />
              );
            })}
          </Group>
        ) : null}
        {type === "stackedBar" || type === "stacked100Bar" ? (
          <Group>
            {stackedSeries.map((stack, colIndex) => {
              const colors = columns[colIndex].colors.split(":");
              const fill = colors.length > 1 ? `url(#pattern-${columns[colIndex].key})` : colors[0];
              return stack.map((d, i) => {
                return (
                  <rect
                    key={`${colIndex}-${i}`}
                    x={xBandScale(xs[i]) ?? 0}
                    y={yScale(d[1]) ?? 0}
                    width={xBandScale.bandwidth()}
                    height={yScale(d[0]) - yScale(d[1])}
                    fill={fill}
                    strokeWidth={1}
                    strokeOpacity={0.15}
                    stroke="#000"
                  />
                );
              });
            })}
          </Group>
        ) : null}
        {tooltipOpen && tooltipData ? (
          <>
            {type === "line" || type === "stackedArea" || type === "stacked100Area" ? (
              <Group>
                {tooltipData.columns.map((col, _i) => {
                  let value = col.value;
                  if (isStack) {
                    value = stackedSeries[col.columnIndex][tooltipData.index][1];
                  }
                  return (
                    <>
                      <Circle
                        key={col.key + "-bg"}
                        cx={xScale(tooltipData.x) ?? 0}
                        cy={yScale(value) ?? 0}
                        r={3}
                        fill={col.colors.split(":")[0]}
                        stroke={"#FFF"}
                        strokeWidth={2.5}
                      />
                      <Circle
                        key={col.key}
                        cx={xScale(tooltipData.x) ?? 0}
                        cy={yScale(value) ?? 0}
                        r={3}
                        fill={col.colors.split(":")[0]}
                        stroke={"#000"}
                        strokeOpacity={0.15}
                        strokeWidth={1}
                      />
                    </>
                  );
                })}
              </Group>
            ) : null}
            <TooltipInPortal
              // key={Math.random()}
              top={tooltipTop}
              left={tooltipLeft}
              style={{
                zIndex: 1000,
                position: "absolute",
              }}
            >
              <TooltipContent
                data={tooltipData}
                period={period}
                isPercent={type === "stacked100Area" || type === "stacked100Bar"}
              />
            </TooltipInPortal>
          </>
        ) : null}
      </svg>
    </Box>
  );
}

const TooltipContent = ({
  data,
  period,
  isPercent,
}: {
  data: TooltipData;
  period: "day" | "week" | "month";
  isPercent: boolean;
}) => {
  const formatDateFull = useCallback(
    (d: Date) => {
      if (period === "day") {
        return format(d, "do MMMM yyyy");
      }
      if (period === "week") {
        return format(d, "'w/b' do MMMM yyyy");
      }
      if (period === "month") {
        return format(d, "MMMM yyyy");
      }
      return "";
    },
    [period],
  );
  const formatValue = useCallback(
    (value: number) => {
      if (isPercent) {
        return formatNumber(value * 100, { maxDecimals: 1 }) + "%";
      }
      return formatNumber(value);
    },
    [isPercent],
  );
  return (
    <Sheet variant="outlined" sx={{ borderRadius: 8, boxShadow: "lg" }}>
      <Stack sx={{ width: 230, paddingBottom: 0.5 }} gap={0}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          sx={{ padding: 0.75, paddingY: 1 }}
        >
          <Typography level="body-sm" fontWeight="bold" lineHeight={1}>
            {formatDateFull(data.x)}
          </Typography>
          <Typography level="body-sm" textColor="neutral.600" lineHeight={1}>
            {formatValue(data.total)}
          </Typography>
        </Stack>
        {data.columns.map((col) => {
          if (!col.value) {
            return;
          }
          const formattedValue = formatValue(col.value);
          return (
            <Stack
              key={col.key}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{ paddingX: 1, paddingY: 0.5 }}
            >
              <Stack direction="row" gap={0.5} alignItems="center">
                <RouteCircle colors={col.colors.split(":")} style={{ width: 18, height: 18 }} />
                <Typography
                  level="body-sm"
                  sx={{
                    maxWidth: 150,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  fontWeight="bold"
                >
                  {col.label}
                </Typography>
              </Stack>
              <Typography level="body-sm" fontWeight="bold">
                {formattedValue}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Sheet>
  );
};
